/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SimpleTooltip.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Modern tooltip component using Radix UI with theme-aware styling,
    auto-wrap, multi-line support, and smooth animations.

 🔧 Notes:
    - Uses @radix-ui/react-tooltip for accessibility and positioning
    - Fully theme-aware (dark/light mode)
    - Supports multi-line text with \n or natural wrapping
    - Smooth fade-in/zoom-in animations
    - Configurable positioning (top, right, bottom, left)
    - Auto-wrap for long text
    - Click/tap to toggle (with auto-hide after 4s)
    - Only one tooltip visible at a time (click-triggered)

 🔁 Last Updated: 2026-02-10 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../lib/utils";
import { useThemeStore, getInputBackgroundColor } from "../../store/theme";

// ─── Global coordination: only one click-opened tooltip at a time ───
// Simple event target so tooltips can tell each other to close.
const tooltipBus = new EventTarget();
let tooltipIdCounter = 0;

interface SimpleTooltipProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  maxWidth?: string;
  title?: string; // (Deprecated) retained for backwards compatibility
}

/**
 * Modern tooltip component with theme awareness and multi-line support
 * Backward compatible with the old SimpleTooltip API
 * 
 * @example
 * <SimpleTooltip content="This is a tooltip\nWith multiple lines" position="top">
 *   <label>Hover or click me</label>
 * </SimpleTooltip>
 */
export const SimpleTooltip: React.FC<SimpleTooltipProps> = ({
  content,
  children,
  className = '',
  position = 'top',
  maxWidth = '400px',
}) => {
  const [open, setOpen] = React.useState(false);
  const autoHideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipIdRef = React.useRef<number>(0);
  const wasClickOpenedRef = React.useRef(false);

  // Assign a stable unique ID on mount
  React.useEffect(() => {
    tooltipIdRef.current = ++tooltipIdCounter;
  }, []);

  // Listen for "close all" events from other tooltips
  React.useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Close this tooltip if another one requested it
      if (detail !== tooltipIdRef.current) {
        setOpen(false);
        wasClickOpenedRef.current = false;
        if (autoHideTimerRef.current) {
          clearTimeout(autoHideTimerRef.current);
          autoHideTimerRef.current = null;
        }
      }
    };
    tooltipBus.addEventListener('closeAll', handler);
    return () => tooltipBus.removeEventListener('closeAll', handler);
  }, []);

  // Clear auto-hide timer on unmount
  React.useEffect(() => {
    return () => {
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    };
  }, []);

  // Handle hover open/close from Radix (don't interfere with click-opened state)
  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (newOpen) {
      setOpen(true);
    } else {
      // Only close on hover-out if it wasn't opened by click
      if (!wasClickOpenedRef.current) {
        setOpen(false);
      }
    }
  }, []);

  // Toggle tooltip on click/tap
  const handleClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing auto-hide timer
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }

    setOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        wasClickOpenedRef.current = true;
        // Tell all other tooltips to close
        tooltipBus.dispatchEvent(new CustomEvent('closeAll', { detail: tooltipIdRef.current }));
        // Auto-hide after 4 seconds
        autoHideTimerRef.current = setTimeout(() => {
          setOpen(false);
          wasClickOpenedRef.current = false;
        }, 4000);
      } else {
        wasClickOpenedRef.current = false;
      }
      return nextOpen;
    });
  }, []);

  // Subscribe to tooltip theme changes to force re-render (optimized)
  const tooltipPrefs = useThemeStore((state) => state.themePrefs.tooltips);
  const themeMode = useThemeStore((state) => state.themePrefs.mode || state.themeMode);
  const lightModeVariant = useThemeStore((state) => state.themePrefs.lightModeVariant || 'white');
  const darkModeVariant = useThemeStore((state) => state.themePrefs.darkModeVariant || 'very-dark-gray');

  // Get the correct input background color for current theme variant
  const inputBgColor = React.useMemo(() => {
    return getInputBackgroundColor(themeMode, lightModeVariant, darkModeVariant);
  }, [themeMode, lightModeVariant, darkModeVariant]);

  // Memoize theme detection to prevent unnecessary re-renders
  const themeInfo = React.useMemo(() => {
    const isDark = themeMode === 'dark';
    const currentTooltipPrefs = isDark ? tooltipPrefs?.darkTheme : tooltipPrefs?.lightTheme;
    return { isDark, currentTooltipPrefs };
  }, [themeMode, tooltipPrefs]);

  const { isDark, currentTooltipPrefs } = themeInfo;

  // Use a stable wrapper to avoid ref composition issues
  const wrapperRef = React.useRef<HTMLSpanElement>(null);

  // If no content, just return children
  if (!content) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Root
      open={open}
      onOpenChange={handleOpenChange}
      delayDuration={200}
      disableHoverableContent={true}
    >
      <TooltipPrimitive.Trigger asChild>
        <span
          ref={wrapperRef}
          className={cn("inline-block w-fit cursor-default", className)}
          style={{ display: 'inline-block' }}
          onClick={handleClick}
        >
          {children}
        </span>
      </TooltipPrimitive.Trigger>

      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={position}
          align="start"
          sideOffset={8}
          collisionPadding={20}
          avoidCollisions={true}
          aria-label={content}
          className={cn(
            // Base styles - tooltip balloon moderno
            "z-[999999] px-4 py-3 text-sm",
            // Responsive: ocultar en pantallas pequeñas para evitar conflictos
            "hidden sm:block",
            // Sombra mejorada y más suave
            "shadow-xl shadow-black/15",
            // Prevenir selección de texto
            "select-none",
            // Tipografía mejorada
            "font-normal leading-relaxed",
            // Animación simple con CSS
            "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
            "data-[state=delayed-open]:fade-in-0 data-[state=closed]:fade-out-0",
            "data-[state=delayed-open]:zoom-in-95 data-[state=closed]:zoom-out-95"
          )}
          style={{
            maxWidth,
            backgroundColor: currentTooltipPrefs?.backgroundColor || inputBgColor,
            color: currentTooltipPrefs?.textColor || (isDark ? '#F1F5F9' : '#1E293B'),
            border: `1px solid ${isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(148, 163, 184, 0.3)'}`,
            borderRadius: `${currentTooltipPrefs?.borderRadius || 0.5}rem`,
          } as React.CSSProperties}
        >
          {/* Contenido sin selección */}
          <div className="pointer-events-none select-none">
            {/* Contenido del tooltip */}
            <span
              className="block whitespace-pre-line break-words leading-relaxed"
              style={{
                color: currentTooltipPrefs?.textColor || (isDark ? '#F1F5F9' : '#1E293B')
              }}
            >
              {content}
            </span>
          </div>

          {/* Flecha nativa de Radix — sin artifacts de borde */}
          <TooltipPrimitive.Arrow
            width={12}
            height={6}
            style={{ fill: currentTooltipPrefs?.backgroundColor || inputBgColor }}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};

export default SimpleTooltip;
